<template>
  <v-container fluid>
    <PageHeaderSlot showBackButton />

    <v-form ref="dataForm" @submit.prevent="">
      <v-card>
        <v-card-text class="px-2">
          <v-row class="ma-0">
            <v-col cols="12" sm="6" lg="3">
              <FormInput
                label="couponCode"
                placeholder="couponCode"
                :fieldValue.sync="formData.code"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                :hint="id === null && !formData.code ? 'couponCodeHints' : ''"
                :enableClear="id === null"
                :disabled="id !== null"
              >
                <template slot="append-inner" v-if="id !== null">
                  <v-btn icon text depressed x-small @click.prevent="gfCopyText(formData.code)">
                    <v-icon class="fi fi-rr-copy" size="15" color="primary"></v-icon>
                  </v-btn>
                </template>
              </FormInput>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <FormInput
                label="couponName"
                placeholder="couponName"
                :fieldValue.sync="formData.name"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <FormSelect
                label="discountType"
                placeholder="discountType"
                :fieldValue.sync="formData.discount_type"
                :options="discountTypeOptions"
                @changed="formData.discount_value = ''"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                :disabled="$validate.DataValid(id)"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <FormDiscount
                :discountValue.sync="formData.discount_value"
                :discountType="formData.discount_type"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                :disabled="$validate.DataValid(id)"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <FormSelect
                label="limitType"
                placeholder="limitType"
                :fieldValue.sync="formData.limit_type"
                :options="limitTypeOptions"
                :required="this.$validate.DataValid(formData.usage_limit)"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                enableClear
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <FormInput
                type="number"
                isIntegerOnly
                label="useLimit"
                placeholder="useLimit"
                :fieldValue.sync="formData.usage_limit"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                :customRules="[v => !$validate.DataValid(v) || parseFloat(v) > 0 || $t('message.valueShouldNotBeZero')]"
                :hint="couponLimitHints"
              />
            </v-col>

            <v-col cols="12" sm="6" lg="3">
              <FormSearchSelect
                label="specificStudent"
                :fieldValue.sync="formData.students"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                multiple
                :options="studentOptions"
                enableClear
                searchPlaceholder="searchStudentData"
                :searching="studentSearching"
                @click-search="searchStudents($event)"
                hint="specificStudentHints"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="d-flex align-center mt-4">
        <FormButton @click="save()" />
        <FormButton
          v-if="$validate.DataValid(id)"
          class="ml-4"
          outlined
          color="error"
          text="delete"
          @click="handleDelete()"
        />
      </div>
    </v-form>
  </v-container>
</template>

<script>
import FormInput from '@/components/formField/FormInput.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDiscount from '@/components/formField/FormDiscount.vue'
import FormSearchSelect from '@/components/formField/FormSearchSelect.vue'

export default {
  name: 'CouponDetails',
  inject: ['getConfirmDialogRef'],
  components: {
    FormInput,
    FormSelect,
    FormDiscount,
    FormSearchSelect,
  },
  computed: {
    id() {
      return this.$validate.regexNumber(this.$route.params.id) ? parseInt(this.$route.params.id) : null
    },
    dispatchUpdateOnChange() {
      return this.$validate.DataValid(this.id)
    },
    discountLabel() {
      let s = this.$t('discount')

      if (
        this.$validate.DataValid(this.formData.discount_type) &&
        this.$validate.regexFloating(this.formData.discount_value)
      ) {
        if (this.formData.discount_type === 'percentage') {
          s += ' ('
          if (this.$i18n.locale === 'en') {
            s += `${this.formData.discount_value}% off`
          } else {
            const off = 100 - parseFloat(this.formData.discount_value)
            s += `${off <= 0 ? 10 : parseFloat((off * 0.1).toFixed(1))}折`
          }
          s += ')'
        } else if (this.formData.discount_type === 'fix_amount') {
          s += ` (-$ ${this.$formatter.formatMoney(this.formData.discount_value)})`
        }
      }

      return s
    },
    couponLimitHints() {
      if (!this.formData.usage_limit) {
        return this.$t('couponLimitEmptyHints')
      }

      if (this.formData.limit_type === 'per_user') {
        return this.$t('couponLimitUserHints', { limit: this.formData.usage_limit })
      } else if (this.formData.limit_type === 'total') {
        return this.$t('couponLimitHints', { limit: this.formData.usage_limit })
      }

      return ''
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    formData: {
      code: '',
      name: '',
      discount_type: '',
      discount_value: '',
      limit_type: '',
      usage_limit: '',
      students: [],
    },

    studentSearching: false,
    studentOptions: [],
    selectedStudentOptions: [],

    discountTypeOptions: [
      { value: 'percentage', text: vm.$t('percentage') },
      { value: 'fix_amount', text: vm.$t('fixAmount') },
    ],
    limitTypeOptions: [
      { value: 'per_user', text: vm.$t('discountLimitTypeOptions.per_user') },
      { value: 'total', text: vm.$t('discountLimitTypeOptions.total') },
    ],
  }),
  methods: {
    async getCouponById() {
      try {
        const data = await this.$Fetcher.GetCouponById(this.id)
        for (const key in this.formData) {
          if (this.$validate.DataValid(data[key])) {
            if (key === 'discount_value' && data.discount_type === 'percentage') {
              this.$set(this.formData, key, 100 - data[key])
            } else if (key === 'usage_limit' && data[key] < 1) {
              this.$set(this.formData, key, '')
            } else {
              this.$set(this.formData, key, data[key])
            }
          }
        }

        await this._getStudentByIds()
      } catch (err) {
        this.$common.error(err)
        if (err !== 'abort') {
          this.showAlert({
            message: 'noData',
            returnLink: 'CouponList',
          })
        }
      }
    },
    async _getStudentByIds() {
      if (this.$validate.DataValid(this.formData.students, true)) {
        this.studentSearching = true
        try {
          const data = await this.$Fetcher.GetStudentsByIdList(this.formData.students)
          this.studentOptions = data.map(item => {
            return { value: item.id, text: `${item.display_student_name} (${item.phone})` }
          })
        } catch {
          this.studentOptions = []
        } finally {
          this.studentSearching = false
        }
      }
    },

    // -- (儲存)
    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const valid = this.$refs['dataForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', { message: 'required' })
        return
      }

      this.$store.dispatch('setLoading', true)
      try {
        // ------------- payload -------------
        let payload = {
          name: this.formData.name,
          discount_type: this.formData.discount_type,
          discount_value: parseFloat(this.formData.discount_value),
          students: this.formData.students,
          limit_type: this.formData.limit_type || '',
          usage_limit: -1,
        }
        if (payload['discount_type'] === 'percentage') {
          payload['discount_value'] = 100 - parseFloat(this.formData.discount_value)
        }
        if (this.$validate.DataValid(this.formData.usage_limit)) {
          payload['usage_limit'] = parseInt(this.formData.usage_limit)
        }

        if (!this.$validate.DataValid(this.id)) {
          payload['code'] = this.formData.code
        }

        // ---------- End of payload -----------

        let message = {}
        if (this.$validate.DataValid(this.id)) {
          await this.$Fetcher.UpdateCoupon(this.id, payload)
          message = {
            message: 'message.saveSuccess',
            refresh: true,
          }
        } else {
          await this.$Fetcher.NewCoupon(payload)
          message = {
            message: 'message.createSuccess',
            redirect: 'CouponList',
          }
        }
        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('toggleSnack', {
          ...message,
          type: 'success',
        })
      } catch (err) {
        this.$common.error(err)
        this.showAlert({
          title: this.$validate.DataValid(this.id) ? 'message.saveFail' : 'message.createFail',
          message: err,
          isError: true,
        })
      }

      this.$store.dispatch('setLoading', false)
    },

    // -- (刪除)
    async handleDelete() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const confirmRef = this.getConfirmDialogRef()
      const confirm = await confirmRef.show({ isDelete: true })
      if (confirm) {
        this.$store.dispatch('setLoading', true)

        try {
          await this.$Fetcher.DeleteCoupon(this.id)
          this.$store.dispatch('toggleSnack', {
            message: 'message.deleteSuccess',
            type: 'success',
            redirect: 'CouponList',
          })
        } catch (err) {
          this.showAlert({
            title: 'message.deleteFail',
            message: err,
            isError: true,
          })
        } finally {
          this.$store.dispatch('setLoading', false)
        }
      }
    },

    async searchStudents(keyword) {
      if (!keyword) {
        this.studentOptions = []
        return
      }

      this.studentSearching = true
      try {
        const { data } = await this.$Fetcher.GetStudents({
          search: keyword,
          filter_item: [{ key: 'status', value: 'active' }],
        })

        this.studentOptions = data.map(item => {
          return { value: item.id, text: `${item.display_student_name} (${item.phone})` }
        })
      } catch {
        this.studentOptions = []
      } finally {
        this.studentSearching = false
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: CouponDetails')
      this.userType = this.gfGetUserType()
      if (this.$validate.DataValid(this.id)) {
        this.getCouponById()
      }
      this.$store.dispatch('toggleLoadingPage', false)
    },
  },
  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else if (this.$store.getters.dataIsUpdated) {
      this.$store.dispatch('showLeaveAlert', { toLink: { name: to.name } })
      next(false)
    } else {
      next()
    }
  },
}
</script>
